import { Box, Paper, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getChartSliceColor } from "../../../utils/distinctColors";

export type KillReasonRow = {
  name: string;
  count: number;
  pct: number;
};

export type KillReasonRowsData = {
  total: number;
  rows: KillReasonRow[];
};

type KillReasonsBreakdownChartProps = {
  data: KillReasonRowsData;
  statusLabel?: string | null;
};

type ChartTooltipProps = {
  active?: boolean;
  payload?: Array<{
    payload?: KillReasonRow;
  }>;
};

function ChartTooltip({ active, payload }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;

  const item = payload[0]?.payload;
  if (!item) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        position: "relative",
        zIndex: 20,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#182737" }}>
        {item.name}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
        {item.count} deals · {Math.round(item.pct)}%
      </Typography>
    </Paper>
  );
}

const ROW_HEIGHT = 34;

export function KillReasonsBreakdownChart({
  data,
  statusLabel,
}: KillReasonsBreakdownChartProps) {
  if (data.total === 0 || data.rows.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          px: 2.5,
          py: 2,
        }}
      >
        <Typography className="text-general-section-title" sx={{ mb: 0.5 }}>
          Kill reasons{statusLabel ? ` (${statusLabel})` : ""}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#182737" }}>
          No deals to summarize.
        </Typography>
      </Paper>
    );
  }

  const chartData = data.rows.map((row, index) => ({
    ...row,
    fill: getChartSliceColor(index),
  }));

  const chartHeight = Math.max(chartData.length * ROW_HEIGHT + 16, 160);
  const labelColumnWidth = 156;

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        background:
          "linear-gradient(180deg, #FAFBFC 0%, #FFFFFF 28%, #FFFFFF 100%)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 1.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography className="text-general-section-title" sx={{ mb: 0.25 }}>
            Kill reasons{statusLabel ? ` · ${statusLabel}` : ""}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Ranked by frequency across filtered deals
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          <Box component="span" sx={{ fontWeight: 700, color: "#182737" }}>
            {data.total}
          </Box>{" "}
          deals
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 1, md: 2 }, py: 2 }}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 0, right: 72, left: 4, bottom: 0 }}
            barCategoryGap="22%"
          >
            <XAxis type="number" hide domain={[0, "dataMax"]} />
            <YAxis
              type="category"
              dataKey="name"
              width={labelColumnWidth}
              tick={{
                fill: "#182737",
                fontSize: 11,
                fontWeight: 500,
              }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={<ChartTooltip />}
              cursor={{ fill: "rgba(14, 70, 100, 0.06)" }}
              wrapperStyle={{ zIndex: 30, pointerEvents: "none" }}
            />
            <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={12}>
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
              <LabelList
                position="right"
                valueAccessor={(entry) => {
                  const row: KillReasonRow = entry.payload;
                  return `${row.count} (${Math.round(row.pct ?? 0)}%)`;
                }}
                style={{
                  fill: "#182737",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
